// React / Next
import { useState, FormEvent } from 'react';
import { Link } from '@tanstack/react-router';

// Components
import { SEO } from '../../components/SEO/SEO';
import { CTAButton } from '../../components/ui/CTAButton/CTAButton';

// Hooks
import {
  useHubSpotForm,
  HUBSPOT_REQUEST_DEMO_CONFIG,
} from '../../hooks/useHubSpotForm';
import { useTrackEvent } from '../../hooks/useTrackEvent';

// Utils
import { isPersonalEmail } from '../../utils/emailValidation';

// Types
type FormData = {
  firstName: string;
  lastName: string;
  workEmail: string;
  phoneNumber: string;
  jobTitle: string;
  firmWebsite: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

/**
 * RequestDemoPage - Demo request form page
 * Allows users to join the waitlist for early access to Archie
 */
export const RequestDemoPage = () => {
  const trackEvent = useTrackEvent();
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    workEmail: '',
    phoneNumber: '',
    jobTitle: '',
    firmWebsite: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // HubSpot form submission hook
  const {
    submit: submitToHubSpot,
    isSubmitting,
    isSuccess: isSubmitted,
    error: hubSpotError,
  } = useHubSpotForm(HUBSPOT_REQUEST_DEMO_CONFIG);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.workEmail.trim()) {
      newErrors.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      newErrors.workEmail = 'Please enter a valid email address';
    } else if (isPersonalEmail(formData.workEmail)) {
      newErrors.workEmail = 'Please use your work email address.';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Submit to HubSpot with mapped field names
    const success = await submitToHubSpot([
      { name: 'firstname', value: formData.firstName },
      { name: 'lastname', value: formData.lastName },
      { name: 'email', value: formData.workEmail },
      { name: 'phone', value: formData.phoneNumber },
      { name: 'jobtitle', value: formData.jobTitle },
      { name: 'website', value: formData.firmWebsite },
    ]);

    if (success) {
      trackEvent('form_submitted', {
        form_name: 'contact_sales',
        form_location: 'request_demo_page',
      });
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center bg-white min-h-[calc(100vh-72px)]">
        <SEO
          title="Thank You | Contact Sales"
          description="Thank you for your interest in Archie. We'll reach out if Archie is a good fit for your accounting firm."
          canonicalUrl="/contact-sales"
        />

        <div className="flex flex-col items-center justify-center gap-[var(--spacing-24)] px-[var(--spacing-24)] py-[var(--spacing-48)] max-w-[600px] text-center">
          <div className="w-16 h-16 rounded-full bg-green-1000/20 flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-green-1000"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <h1 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000">
            You're on the list!
          </h1>

          <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
            Thank you for your interest in Archie. We'll reach out as soon as
            your firm is selected for early access.
          </p>

          <Link
            to="/"
            className="font-body text-[16px] leading-[24px] text-orange-800 hover:text-orange-1000 underline underline-offset-4 transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center bg-white min-h-[calc(100vh-72px)]">
      <SEO
        title="Contact Sales"
        description="We review every request and will reach out if Archie is a good fit for your accounting firm."
        canonicalUrl="/contact-sales"
      />

      {/* Centered Content Wrapper */}
      <div className="flex items-center justify-center w-full flex-1 py-[var(--spacing-32)]">
        {/* Main Content */}
        <section className="flex flex-col lg:flex-row lg:items-start gap-[var(--spacing-64)] px-[var(--spacing-24)] w-full max-w-[1224px] max-lg:gap-[var(--spacing-48)] max-md:px-[var(--spacing-16)]">
          {/* Left Column - Text */}
          <div className="flex flex-col gap-[var(--spacing-24)] lg:w-[392px] lg:shrink-0">
            <h1 className="font-heading text-[40px] leading-[48px] tracking-[-0.5px] font-light text-blue-1000">
              Contact Sales
            </h1>

            <div className="flex flex-col gap-[var(--spacing-16)]">
              <p className="font-heading text-[24px] leading-[32px] font-light text-blue-1000">
                We review every request and will reach out if Archie is a good
                fit for your accounting firm.
              </p>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="flex-1">
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-[var(--spacing-24)]"
              noValidate
            >
              {/* Name Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--spacing-16)]">
                <InputField
                  label="First Name"
                  value={formData.firstName}
                  onChange={(value) => handleInputChange('firstName', value)}
                  error={errors.firstName}
                  required
                  autoComplete="given-name"
                />
                <InputField
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(value) => handleInputChange('lastName', value)}
                  error={errors.lastName}
                  required
                  autoComplete="family-name"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[var(--spacing-16)]">
                <InputField
                  label="Work Email"
                  type="email"
                  value={formData.workEmail}
                  onChange={(value) => handleInputChange('workEmail', value)}
                  error={errors.workEmail}
                  required
                  autoComplete="email"
                />
                <InputField
                  label="Phone Number"
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(value) => handleInputChange('phoneNumber', value)}
                  error={errors.phoneNumber}
                  autoComplete="tel"
                />
              </div>

              {/* Job Title - Full Width */}
              <InputField
                label="Job Title"
                value={formData.jobTitle}
                onChange={(value) => handleInputChange('jobTitle', value)}
                error={errors.jobTitle}
                required
                autoComplete="organization-title"
              />

              {/* Firm Website - Full Width */}
              <InputField
                label="Firm Website"
                type="url"
                value={formData.firmWebsite}
                onChange={(value) => handleInputChange('firmWebsite', value)}
                error={errors.firmWebsite}
                autoComplete="url"
                placeholder="https://"
              />

              {/* Terms Agreement */}
              <p className="font-body text-[16px] leading-[24px] text-blue-1000 text-center">
                By registering you agree to Archie's{' '}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 hover:text-orange-800 transition-colors"
                >
                  Privacy Policy
                </Link>{' '}
                and{' '}
                <Link
                  to="/terms-of-service"
                  className="underline underline-offset-2 hover:text-orange-800 transition-colors"
                >
                  Terms of Service
                </Link>
                .
              </p>

              {/* HubSpot Error Message */}
              {hubSpotError && (
                <p
                  className="font-body text-[14px] leading-[20px] text-red-1000 text-center"
                  role="alert"
                >
                  {hubSpotError}
                </p>
              )}

              {/* Submit Button */}
              <div className="flex justify-center">
                <CTAButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Request a discussion'}
                </CTAButton>
              </div>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
};

// Types
type InputFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: 'text' | 'email' | 'tel' | 'url';
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
};

/**
 * InputField - Reusable form input component matching Figma design
 */
const InputField = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  required = false,
  autoComplete,
  placeholder,
}: InputFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const hasValue = value.length > 0;

  return (
    <div className="flex flex-col gap-[var(--spacing-4)]">
      <div
        className={`
          relative h-[56px]
          px-[var(--spacing-16)]
          border rounded-[var(--radius-8)]
          transition-colors duration-200
          ${error ? 'border-red-1000' : isFocused ? 'border-orange-800' : 'border-blue-1000'}
        `}
      >
        {/* Label - hidden when input has value */}
        {!hasValue && (
          <label
            className="absolute left-[var(--spacing-16)] top-1/2 -translate-y-1/2 pointer-events-none font-body text-[16px] leading-[24px] text-blue-1000"
          >
            {label}
            {required && <span className="text-red-1000 ml-0.5">*</span>}
          </label>
        )}

        {/* Input */}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          className="absolute inset-0 w-full h-full px-[var(--spacing-16)] bg-transparent outline-none font-body text-[16px] leading-[24px] text-blue-1000 placeholder:text-transparent"
          aria-label={label}
          aria-invalid={!!error}
          aria-describedby={error ? `${label}-error` : undefined}
        />
      </div>

      {/* Error Message */}
      {error && (
        <p
          id={`${label}-error`}
          className="font-body text-[12px] leading-[16px] text-red-1000"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};

